import useSWR, { mutate } from "swr";

import * as fetcher from "../fetcher";

export type CalendarZoom = "NORMAL" | "ZOOM_OUT";
export type CalendarView = "AGENDA" | "OVERVIEW" | "TEAM";

export interface CalendarSettingsPayload {
  showOutlookCalendar: boolean;
  firstDayOfWeek: "sunday" | "monday";
  preferredCalendarView: CalendarView;
  showWeekNumber: boolean;
  zoom: CalendarZoom;
  selectedTeam?: number;
  deselectedMembersByTeam?: Record<number, string[]>;
}

export type CalendarSettings = CalendarSettingsPayload & {
  zoomWidth: number;
};

export interface CalendarFilters {
  filteredTasks: string[];
  hideCanceledEvents: boolean;
  hideCompletedEvents: boolean;
  hideScheduleBlocks: boolean;
  hideHiddenActivity: boolean;
  filteredScheduleTypes: string[];
}

type CalendarFiltersPayload = Omit<
  CalendarFilters,
  "hideScheduleBlocks" | "hideHiddenActivity"
> & {
  showScheduleBlocks: boolean;
  showHiddenActivity: boolean;
};

const toCalendarFilters = (
  payload: CalendarFiltersPayload
): CalendarFilters => {
  const { showScheduleBlocks, showHiddenActivity, ...rest } = payload;
  return {
    ...rest,
    hideScheduleBlocks: !showScheduleBlocks,
    hideHiddenActivity: !showHiddenActivity,
  };
};

const toCalendarFiltersPayload = (
  filters: CalendarFilters
): CalendarFiltersPayload => {
  const { hideScheduleBlocks, hideHiddenActivity, ...rest } = filters;
  return {
    ...rest,
    showScheduleBlocks: !hideScheduleBlocks,
    showHiddenActivity: !hideHiddenActivity,
  };
};

const ZOOM_WIDTH_KEY = "saleslead-calendar-zoomWidth";

const getZoomWidth = (): number => {
  const stored = window.localStorage.getItem(ZOOM_WIDTH_KEY);
  return stored ? Number(stored) : 1000;
};

export const setZoomWidth = (zoomWidth: number) => {
  window.localStorage.setItem(ZOOM_WIDTH_KEY, String(zoomWidth));
};

export const defaultCalendarSettings: CalendarSettings = {
  showOutlookCalendar: true,
  firstDayOfWeek: "monday",
  preferredCalendarView: "AGENDA",
  showWeekNumber: true,
  zoom: "ZOOM_OUT",
  zoomWidth: 1000,
  selectedTeam: undefined,
  deselectedMembersByTeam: {},
};

export const defaultCalendarFilters: CalendarFilters = {
  filteredTasks: [],
  hideCanceledEvents: false,
  hideCompletedEvents: false,
  hideScheduleBlocks: false,
  hideHiddenActivity: true,
  filteredScheduleTypes: [],
};

export const useCalendarSettings = () => {
  const { data, ...rest } = useSWR<CalendarSettings>(
    "/coworkers/calendarSettings",
    async () => {
      try {
        const response =
          await fetcher.coworker.get<CalendarSettingsPayload>(
            "/calendarSettings"
          );
        return { ...response, zoomWidth: getZoomWidth() };
      } catch {
        return { ...defaultCalendarSettings, zoomWidth: getZoomWidth() };
      }
    },
    { revalidateOnFocus: false }
  );
  return {
    data: data ?? { ...defaultCalendarSettings, zoomWidth: getZoomWidth() },
    ...rest,
  };
};

export const saveCalendarSettings = async (
  settings: CalendarSettingsPayload
): Promise<void> => {
  await fetcher.coworker.update("/calendarSettings", settings);
  mutate("/coworkers/calendarSettings", settings, { revalidate: false });
};

export const useCalendarFilters = () => {
  const { data, ...rest } = useSWR<CalendarFilters>(
    "/coworkers/calendarFilter",
    async () => {
      try {
        const response =
          await fetcher.coworker.get<CalendarFiltersPayload>("/calendarFilter");
        return toCalendarFilters(response);
      } catch {
        return defaultCalendarFilters;
      }
    },
    { revalidateOnFocus: false }
  );
  return { data: data ?? defaultCalendarFilters, ...rest };
};

export const saveCalendarFilters = async (
  filters: CalendarFilters
): Promise<void> => {
  await fetcher.coworker.update(
    "/calendarFilter",
    toCalendarFiltersPayload(filters)
  );
  mutate("/coworkers/calendarFilter", filters, { revalidate: false });
};
